'use client';

import TanstackBackgroundLogo from '@/assets/images/tanstack-background-logo.svg';
import TanstackLogo from '@/assets/images/tanstack-logo.svg';
import { isLocal } from '@/constants/server-runtime-flags';
import { useFeatureFlags } from '@/hooks/state/feature-flags';
import { clientErrorLoggerApiService } from '@/services/(frontend)/client-error-logger-api.service';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense, useState } from 'react';

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => {
    void clientErrorLoggerApiService.logError(
      new Error('ReactQueryDevtools loaded in non-local environment, be sure you know what you\'re doing')
    );
    return { default: d.ReactQueryDevtools };
  })
);

export const QueryDevtoolsProvider = (): React.JSX.Element | null => {
  const [showDevtools, setShowDevtools] = useState<boolean>(false);
  const { data: featureFlags } = useFeatureFlags();

  if (!featureFlags?.enableTanStackDevTools) {
    return null;
  }

  return <>
    {isLocal && <ReactQueryDevtools initialIsOpen={false} />}

    {!showDevtools && !isLocal &&
      <div className="fixed bottom-2 right-2 z-[100000] p-1 text-left flex items-center justify-center rounded-full shadow-md overflow-hidden">
        <div aria-hidden="true"
          className="absolute -top-2 -left-2 -right-2 -bottom-2 rounded-full blur-[6px] saturate-[1.2] contrast-[1.1]"
        >
          <TanstackBackgroundLogo className="absolute w-full h-full" />
        </div>
        <button aria-label="Open React Query Devtools"
          className="relative z-[1] p-0 rounded-full bg-transparent border-none h-[40px] flex w-[40px] overflow-hidden cursor-pointer outline-none"
          data-test="button-open-react-query-devtools"
          title="Open React Query Devtools"
          onClick={() => setShowDevtools(true)}
        >
          <TanstackLogo className="absolute w-full h-full" />
          <span className="sr-only">Open React Query Devtools</span>
        </button>
      </div>
    }

    {showDevtools &&
      <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction initialIsOpen />
      </Suspense>
    }
  </>;
};
